import { useDispatch, useSelector } from "react-redux";
import { CustomizationProvider, CustomizationProviderProps } from "@twilio-paste/core/customization";
import { CSSProperties, FC, useEffect } from "react";

import { AppState, EngagementPhase } from "../../store/definitions";
import { sessionDataHandler } from "../../sessionDataHandler";
import { initSession } from "../../store/actions/initActions";
import { changeEngagementPhase } from "../../store/actions/genericActions";
import { WebChatContainer } from "./WebChatContainer";

const AnyCustomizationProvider: FC<CustomizationProviderProps & { style: CSSProperties }> = CustomizationProvider;

/** 从 URL 解析模板跳转带回的会话 SID（微信 OAuth 的 state，或显式 conversationSid） */
const resolveTargetConversationSid = () => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = (params.get("conversationSid") || "").trim();
    const fromState = (params.get("state") || "").trim();
    if (fromQuery.startsWith("CH")) {
        return fromQuery;
    }
    if (fromState.startsWith("CH")) {
        return fromState;
    }
    return "";
};

export function WebChat() {
    const theme = useSelector((state: AppState) => state.config.theme);
    const dispatch = useDispatch();

    useEffect(() => {
        let cancelled = false;

        const bootstrap = async () => {
            const targetSid = resolveTargetConversationSid();

            // 模板「查看详情」：直达该会话，但不写入 localStorage，避免关微信后从菜单自动进窗
            if (targetSid) {
                sessionDataHandler.setPersistToStorage(false);
                dispatch(changeEngagementPhase({ phase: EngagementPhase.Loading }));
                try {
                    const data = await sessionDataHandler.resumeConversation(targetSid);
                    if (!cancelled) {
                        await dispatch(
                            initSession({
                                token: data.token,
                                conversationSid: data.conversationSid,
                                name: "",
                                query: ""
                            }) as any
                        );
                    }
                    return;
                } catch (e) {
                    console.warn("直达会话失败，回退到表单页", e);
                    if (!cancelled) {
                        dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
                    }
                    return;
                }
            }

            // 服务号菜单 / 普通进首页：立刻表单；仅恢复「表单进线」写入的未过期 Session
            sessionDataHandler.setPersistToStorage(true);
            dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));

            const data = sessionDataHandler.tryResumeExistingSession();
            if (!data || cancelled) {
                return;
            }
            try {
                await dispatch(
                    initSession({
                        token: data.token,
                        conversationSid: data.conversationSid,
                        name: "",
                        query: ""
                    }) as any
                );
            } catch (e) {
                console.warn("后台恢复会话失败，留在表单页", e);
                if (!cancelled) {
                    dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
                }
            }
        };

        bootstrap();
        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    return (
        <AnyCustomizationProvider
            baseTheme={theme?.isLight ? "default" : "dark"}
            theme={theme?.overrides}
            elements={{
                MESSAGE_INPUT: {
                    boxShadow: "none!important" as "none"
                },
                MESSAGE_INPUT_BOX: {
                    display: "inline-block",
                    boxShadow: "none"
                },
                ALERT: {
                    paddingTop: "space30",
                    paddingBottom: "space30"
                },
                BUTTON: {
                    "&[aria-disabled='true'][color='colorTextLink']": {
                        color: "colorTextLinkWeak"
                    }
                }
            }}
            style={{ minHeight: "100%", minWidth: "100%" }}
        >
            <WebChatContainer />
        </AnyCustomizationProvider>
    );
}
